import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Edit, X, Upload, Loader } from 'lucide-react';
import {
  fetchAbout,
  updateAbout
} from '../../lib/sanityClient';
import SuccessToast from './SuccessToast';
import ErrorAlert from './ErrorAlert';

export default function AdminAbout({ refreshTrigger = 0 }) {
  const [data, setData] = useState({
    title: '',
    bio: '',
    image: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [editData, setEditData] = useState({ ...data });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const loadData = async () => {
    try {
      setLoading(true);
      const aboutData = await fetchAbout();
      if (aboutData) {
        setData(aboutData);
        setEditData(aboutData);
        if (aboutData.image?.asset?.url) {
          setImagePreview(aboutData.image.asset.url);
        }
      }
      setError('');
    } catch (err) {
      setError('Kunne ikke laste om-seksjonen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditData(data);
    setImagePreview(data.image?.asset?.url || null);
    setNewImageFile(null);
    setIsEditing(true);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Filen må være et bilde');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('Bildet er for stort. Maks 5MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      setNewImageFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      if (!editData.title.trim() || !editData.bio.trim()) {
        setError('Både navn og biografi må fylles ut');
        setSubmitLoading(false);
        return;
      }

      await updateAbout(editData, newImageFile);
      setSuccessMessage('Om-seksjonen oppdatert!');
      await loadData();
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err.message || 'En feil oppstod');
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-Pink-main"></div>
      </div>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={itemVariants}>
      <ErrorAlert message={error} onClose={() => setError('')} />

      <h2 className="text-2xl font-semibold text-Neautral-main font-opensans mb-6">
        Om Studio Søraa
      </h2>

      {/* Edit Mode */}
      {isEditing ? (
        <motion.div
          className="bg-Pink-BG rounded-[15px] p-4 sm:p-6 md:p-8 border-2 border-Pink-main"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-Neautral-main font-opensans">
              Rediger Om-seksjonen
            </h3>
            <motion.button
              onClick={() => setIsEditing(false)}
              className="text-Neautral-light hover:text-Neautral-main transition"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <X size={24} strokeWidth={2} />
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 md:grid md:grid-cols-3 md:gap-6 md:space-y-0">
            {/* Left: Image */}
            <div className="md:col-span-1 flex flex-col">
              <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                Profilbilde
              </label>
              <div className="space-y-4">
                {imagePreview && (
                  <div className="relative w-full">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full rounded-[10px] border-2 border-Neautral-base object-cover"
                    />
                    <motion.button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setNewImageFile(null);
                      }}
                      className="absolute top-2 right-2 p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <X size={18} strokeWidth={2} />
                    </motion.button>
                  </div>
                )}

                <label className="flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-[10px] border-2 border-dashed border-Neautral-base bg-Neautral-white cursor-pointer hover:border-Pink-main transition">
                  <Upload size={24} className="text-Neautral-light" strokeWidth={1.5} />
                  <p className="text-xs text-center text-Neautral-main font-opensans font-semibold">
                    Klikk for å velge bilde
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Right: Form Fields */}
            <div className="md:col-span-2 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  Navn *
                </label>
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  placeholder="f.eks. Sarah Søraa"
                  className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  Biografi *
                </label>
                <textarea
                  value={editData.bio}
                  onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                  placeholder="Skriv om deg selv og ditt studioet..."
                  rows="5"
                  className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans resize-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex flex-col-reverse sm:flex-row gap-4 justify-end pt-4">
                <motion.button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  disabled={submitLoading}
                  className="px-6 py-2 rounded-[10px] bg-Neautral-base text-Neautral-main font-semibold font-opensans hover:bg-Neautral-light transition disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Avbryt
                </motion.button>
                <motion.button
                  type="submit"
                  disabled={submitLoading}
                  className="px-6 py-2 rounded-[10px] bg-Pink-main text-white font-semibold font-opensans hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitLoading ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      <span className="hidden sm:inline">Lagrer...</span>
                      <span className="sm:hidden">Lagrer</span>
                    </>
                  ) : (
                    'Lagre endringer'
                  )}
                </motion.button>
              </div>
            </div>
          </form>
        </motion.div>
      ) : (
        /* View Mode */
        <motion.div
          className="bg-Neautral-white rounded-[15px] p-4 sm:p-6 md:p-8 shadow-[0px_10px_25px_0px_rgba(126,126,126,0.15)]"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-start gap-4 md:gap-6">
            <div className="flex-1 order-2 md:order-1">
              <h3 className="text-xl sm:text-2xl font-semibold text-Neautral-main font-opensans mb-4">
                {data.title}
              </h3>
              <p className="text-sm sm:text-base text-Neautral-main font-opensans whitespace-pre-wrap">
                {data.bio}
              </p>
            </div>

            {data.image?.asset?.url && (
              <div className="flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 order-1 md:order-2">
                <img
                  src={data.image.asset.url}
                  alt={data.title}
                  className="w-full h-full rounded-[10px] object-cover border-2 border-Neautral-base"
                />
              </div>
            )}
          </div>

          <motion.button
            onClick={handleEdit}
            className="mt-6 flex items-center gap-2 px-6 py-3 rounded-[15px] bg-Pink-main text-white font-semibold font-opensans hover:opacity-90 transition"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Edit size={20} strokeWidth={2} />
            Rediger
          </motion.button>
        </motion.div>
      )}

      {/* Success Toast */}
      <SuccessToast
        message={successMessage}
        isVisible={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </motion.div>
  );
}
